import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import Clear from '@mui/icons-material/Clear';
import Info from '@mui/icons-material/Info';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { forwardRef, type ReactNode, useEffect, useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

type TextMaskCustomProps = InputBaseComponentProps & {
    mask?: InputTextProps['mask'];
}

const TextMaskCustom = forwardRef<HTMLInputElement, TextMaskCustomProps>(
    function TextMaskCustom(props, ref) {
        const { id, mask, onChange, placeholder, ...other } = props;
        return (
            <IMaskInput
                {...other}
                definitions={mask?.definitions}
                id={id}
                inputRef={ref}
                mask={mask?.mask}
                onAccept={(value) => onChange({ target: { id, name: id, value } })}
                placeholder={!isUndefinedNullOrEmpty(placeholder) ? placeholder : undefined}
                placeholderChar={mask?.placeholderChar || '_'}
                type="text"
            />
        );
    },
);

export type InputTextProps = {
    autoWidth?: boolean;
    autoComplete?: string;
    blink?: boolean;
    callback?: (params: { id: string; val: string }) => void;
    className?: string;
    clearBtn?: boolean;
    disabled?: boolean;
    edit?: boolean;
    endButton?: ReactNode;
    endIcon?: IconifyProps;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    hideLabelEdit?: boolean;
    icon?: IconifyProps;
    id?: string;
    info?: string;
    l?: string;
    loading?: boolean;
    mask?: {
        definitions?: Record<string, RegExp>;
        mask: string;
        placeholderChar?: string;
    };
    maxLength?: number;
    onKeyUp?: () => void;
    outlined?: boolean;
    testid?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    type?: 'text' | 'multiline' | 'tel' | 'email' | 'fax';
    upperCase?: boolean;
    v?: string;
    vNew?: string;
    widthElement?: string;
};

export function convertTextValue(v: InputTextProps['v']): string {
    return v;
}

let blinkTimer: NodeJS.Timeout;

const InputText = (moduleProps: InputTextProps) => {
    const {
        autoWidth,
        autoComplete,
        blink,
        callback,
        clearBtn,
        disabled,
        edit,
        endButton,
        endIcon,
        error,
        fatLabel,
        helperText,
        hideLabel,
        hideLabelEdit,
        icon,
        id,
        info,
        l,
        loading,
        mask,
        maxLength,
        onKeyUp,
        outlined,
        textAlign,
        upperCase,
        testid,
        type,
        v,
        vNew,
        widthElement,
    } = {
        autoWidth: false,
        blink: false,
        disabled: false,
        edit: false,
        fatLabel: false,
        hideLabel: false,
        hideLabelEdit: false,
        loading: false,
        outlined: false,
        type: 'text',
        upperCase: false,
        ...moduleProps,
    };
    const [value, setValue] = useState<InputTextProps['v']>(v);
    const theme: CustomTheme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    const getValue = (val) => {
        if (edit) return !checkUndefinedNullOrEmpty(val) ? val : '';
        else if (loading) {
            return <Skeleton animation="wave" width="100%" height="calc(1em + 8px)" />;
        } else if (!checkUndefinedNullOrEmpty(val)) {
            if (type === 'multiline') {
                const parts = val.split(/\n/);
                const output = [];
                for (let i = 0; i < parts.length; i++) {
                    output.push(
                        <p key={'inputText' + id + '_multilineLine' + i} style={{ marginBottom: 0, wordBreak: 'break-word' }}>
                            {parts[i]}
                        </p>
                    );
                }

                return output;
            } else return val;
        }
        return '-';
    };

    /**
     * Calls the Callback-Function with a Number (Singleselect) or an Array (Multiselect) as Callback-Object {id: [id], val: [returnValue]}
     * @param {String} val - Value to Send to Callback-Function
     */
    const handleSubmit = (val) => {
        if (callback) callback({ id: id, val: val });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputText_' + id);
    };

    /**
     * Handles the Changes of state for the input element
     * @param {Object} e - Event-Object
     */
    const handleChange = (e) => {
        const newValue = e.target.value || '';
        setValue(newValue);
        handleSubmit(newValue);
    };

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                clearTimeout(blinkTimer);
                blinkTimer = setTimeout(() => {
                    containerRef?.current?.scrollIntoView({ block: 'center' });
                    clearTimeout(blinkTimer);
                }, 2000);
            }
        },
        [blink]
    );

    return edit ? (
        <div
            className={[blink ? 'blinkInputfield' : '', 'inputText'].join(' ')}
            ref={containerRef}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : textAlign === 'justify' ? 'space-between' : 'flex-start',
                width: !autoWidth ? '100%' : widthElement,
            }}
        >
            {(loading || icon) && (
                <div
                    className="icon materialIcon iconMarginRight"
                    style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginTop: 20, marginBottom: -20 }}
                >
                    {loading ? <CircularProgress color="primary" size="1.5rem" /> : icon ? <Iconify icon={icon} /> : undefined}
                </div>
            )}
            <FormControl fullWidth>
                <TextField
                    className={outlined && (loading || icon) ? 'outlinedStartAdornment' : undefined}
                    variant={outlined ? 'outlined' : undefined}
                    id={id}
                    type={type}
                    multiline={type === 'multiline'}
                    value={!checkUndefined(value) ? getValue(value) : null}
                    label={!hideLabelEdit ? l : undefined}
                    onChange={handleChange}
                    onKeyUp={(e) => {
                        if (!checkUndefinedOrNull(onKeyUp) && e.key === 'Enter') {
                            e.preventDefault();
                            onKeyUp();
                        }
                    }}
                    autoComplete={autoComplete ? 'section-ahdcars ' + autoComplete : 'off'}
                    error={!loading && !checkUndefinedOrNull(error)}
                    helperText={
                        !loading
                            ? !checkUndefinedOrNull(error)
                                ? error
                                : !checkUndefinedNullOrEmpty(helperText)
                                  ? helperText
                                  : !checkUndefinedNullOrEmpty(maxLength)
                                    ? (value || '').length + '/' + maxLength
                                    : undefined
                            : undefined
                    }
                    inputProps={{
                        mask,
                        maxLength: maxLength,
                        placeholder: hideLabelEdit ? l : '',
                        style: { textTransform: upperCase ? 'uppercase' : undefined, textAlign: textAlign },
                    }}
                    disabled={loading || disabled}
                    InputProps={{
                        inputComponent: !isUndefinedNullOrEmpty(mask) ? TextMaskCustom : undefined,
                        startAdornment: outlined ? (
                            loading || icon ? (
                                <InputAdornment data-testid="icon" position="start" style={{ paddingRight: 5 }}>
                                    {loading ? (
                                        <CircularProgress color="primary" size="1.5rem" />
                                    ) : (
                                        <span style={{ lineHeight: 1 }}>
                                            <Iconify icon={icon} />
                                        </span>
                                    )}
                                </InputAdornment>
                            ) : undefined
                        ) : undefined,
                        endAdornment: outlined ? (
                            <InputAdornment data-testid="clear" position="end" style={{ paddingLeft: 5 }}>
                                {clearBtn && !disabled && !checkUndefinedNullOrEmpty(getValue(value)) ? (
                                    <IconButton
                                        onClick={function () {
                                            handleChange({ target: { value: undefined } });
                                        }}
                                    >
                                        <Clear />
                                    </IconButton>
                                ) : undefined}
                                {!checkUndefinedNullOrEmpty(endButton) ? endButton : undefined}
                                {!checkUndefinedNullOrEmpty(endIcon) ? <Iconify icon={endIcon} /> : undefined}
                                {!checkUndefinedNullOrEmpty(info) && (
                                    <Tooltip title={info}>
                                        <span>
                                            <Info color="primary" />
                                        </span>
                                    </Tooltip>
                                )}
                            </InputAdornment>
                        ) : undefined,
                    }}
                    InputLabelProps={{
                        shrink: !checkUndefinedNullOrEmpty(value),
                    }}
                />
            </FormControl>
            {!outlined && clearBtn && !disabled && !checkUndefinedNullOrEmpty(getValue(value)) && (
                <div
                    className="clearBtn materialIcon"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: hideLabelEdit ? undefined : 20,
                        marginBottom: hideLabelEdit ? undefined : -20,
                    }}
                >
                    <IconButton
                        onClick={function () {
                            handleChange({ target: { value: undefined } });
                        }}
                    >
                        <Clear />
                    </IconButton>
                </div>
            )}
            {!outlined && !checkUndefinedNullOrEmpty(endButton) && (
                <div
                    className="endButton materialIcon"
                    style={{ display: 'flex', alignItems: 'center', color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8 }}
                >
                    {endButton}
                </div>
            )}
            {!outlined && !checkUndefinedNullOrEmpty(endIcon) && (
                <div
                    className="endIcon materialIcon"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: hideLabelEdit ? undefined : 20,
                        marginBottom: hideLabelEdit ? undefined : -20,
                    }}
                >
                    <Iconify icon={endIcon} />
                </div>
            )}
            {!outlined && !checkUndefinedNullOrEmpty(info) && (
                <div
                    className="infoIcon materialIcon"
                    style={{
                        color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: hideLabelEdit ? undefined : 20,
                        marginBottom: hideLabelEdit ? undefined : -20,
                    }}
                >
                    <Tooltip title={info}>
                        <span>
                            <Info color="primary" />
                        </span>
                    </Tooltip>
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            alignItems={type === 'multiline' ? 'flex-start' : undefined}
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={getValue(v)}
            valueSx={{ textTransform: upperCase ? 'uppercase' : undefined, whiteSpace: 'pre-wrap' }}
        />
    );
};

export default InputText;
